import { gql } from "lib/apolloClient";
import {
  configurationFragment,
  curriculumProgramFragment,
  videoMeetingToastEventFragment,
} from "./ChildrenPageFragments";
import { organizationFragment } from "modules/CommonFragments";

export const getConfigurationQuery = gql`
  query getConfiguration(
    $configurationEntityGroupFilters: [ConfigurationEntityGroupFilter!]
    $isAdminPage: Boolean!
    $isSettingsPage: Boolean!
    $isCourseModuleSetting: Boolean
    $isAdminNotificationManager: Boolean
  ) {
    platform {
      configuration(
        filters: {
          configurationEntityGroupFilters: $configurationEntityGroupFilters
          isSettingsPage: $isSettingsPage
        }
      ) {
        ...configurationResponseItem
      }
    }
  }
  ${configurationFragment.configurationResponse}
`;

export const getPreferenceQuery = gql`
  query getPreferences(
    $configurationEntityGroupFilters: [ConfigurationEntityGroupFilter!]
    $isAdminPage: Boolean!
    $isSettingsPage: Boolean!
    $isCourseModuleSetting: Boolean
    $isAdminNotificationManager: Boolean
  ) {
    platform {
      preferences(
        filters: {
          configurationEntityGroupFilters: $configurationEntityGroupFilters
        }
      ) {
        ...configurationResponseItem
      }
    }
  }
  ${configurationFragment.preferenceResponse}
`;

export const getStudentsCurriculumQuery = gql`
  query getStudentsCurriculum(
    $ids: [ID!]
    $studentCurriculumProgramFilters: StudentCurriculumProgramFilter
  ) {
    platform {
      student(ids: $ids) {
        curriculumPrograms(filters: $studentCurriculumProgramFilters) {
          ...curriculumProgramBasicDetailsWithSubscriptionPlanItem
        }
      }
    }
  }
  ${curriculumProgramFragment.curriculumProgramBasicDetailsWithSubscriptionPlan}
`;

export const getStaffCurriculumQuery = gql`
  query getStaffCurriculum(
    $ids: [ID!]
    $userCurriculumProgramFilter: UserCurriculumProgramFilter
  ) {
    platform {
      staff(ids: $ids) {
        curriculumPrograms(filters: $userCurriculumProgramFilter) {
          ...curriculumProgramBasicDetailsWithSubscriptionPlanItem
        }
      }
    }
  }
  ${curriculumProgramFragment.curriculumProgramBasicDetailsWithSubscriptionPlan}
`;

export const getStaffAcademicYearCurriculumMapQuery = gql`
  query getStaffAcademicYearCurriculumMap(
    $ids: [ID!]
    $filters: AcademicYearCurriculumMapFilters
  ) {
    platform {
      staff(ids: $ids) {
        id
        academicYearCurriculumMap(filters: $filters) {
          academicYear {
            id
            isCurrentAcademicYear
          }
          curriculumPrograms {
            id
            type
            nodeInterfaceType
            label
            acronym
            academicSetupType
            subscriptionPlan {
              id
              type
              isFree
              label
            }
            curriculumProgramData {
              subscriptionPlan
            }
            programType
            logo
          }
        }
      }
    }
  }
`;

export const getPlatformUserDetailsQuery = gql`
  query getPlatformUserDetails($id: ID!, $type: ENTITY_TYPE_ENUM!) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        id
        firstName
        middleName
        lastName
        preferredName
        pronouns
        suffix
        prefix
        displayRole
        profileImage
        email
        type
        showPitchScreen
        role
        showClassSelectionScreen
        showBifurcationScreen
        isDevUser
        isArchived
        entityTags {
          id
          key
          value
        }
        coachMarkStatus {
          id
          coachMark
          isViewed
          count
        }
        firstLoggedInAt
        type
        userDetailsHash {
          appId
          sprinklrChatHash
          userServiceId
          organizationId
        }
        phoneNumber
        designation {
          id
          title
        }
        academicYears(filters: { archivalState: ACTIVE }) {
          id
          startDate
          endDate
          isCurrentAcademicYear
          isUpcomingAcademicYear
          transitionReviewStatus
        }
        jwt
        intercomJwt
        isImpersonated
        linkedIdentityUsersV2 {
          totalCount
        }
        linkedIdentityV2 {
          id
        }
        organization {
          id
          name
          tier {
            plan
            isFree
          }
          region
          academicYears {
            id
            isCurrentAcademicYear
          }
          curriculumPrograms {
            ...curriculumProgramBasicDetailsItem
            ...curriculumProgramBasicDetailsWithSubscriptionPlanItem
          }
        }
      }
      ... on Student {
        id
        firstName
        middleName
        lastName
        pronouns
        preferredName
        prefix
        suffix
        profileImage
        email
        type
        isDevUser
        isArchived
        sourceId
        type
        coachMarkStatus {
          id
          coachMark
          isViewed
          count
        }
        type
        userDetailsHash {
          appId
          sprinklrChatHash
          userServiceId
          organizationId
        }
        phoneNumber
        allCourses {
          id
          academicYears {
            id
          }
          curriculumProgram {
            ...curriculumProgramBasicDetailsItem
          }
        }
        academicYears(filters: { archivalState: ACTIVE }) {
          id
          startDate
          endDate
          isCurrentAcademicYear
          isUpcomingAcademicYear
          transitionReviewStatus
        }
        jwt
        isImpersonated
        linkedIdentityUsersV2 {
          totalCount
        }
        organization {
          id
          name
          tier {
            plan
            isFree
          }
          region
          academicYears {
            id
            isCurrentAcademicYear
          }
          curriculumPrograms {
            ...curriculumProgramBasicDetailsItem
            ...curriculumProgramBasicDetailsWithSubscriptionPlanItem
          }
        }
        yearGroup {
          id
          grade {
            id
          }
        }
      }
      ... on FamilyMember {
        id
        firstName
        middleName
        lastName
        preferredName
        prefix
        suffix
        pronouns
        profileImage
        type
        Email: email
        isDevUser
        status
        userDetailsHash {
          appId
          sprinklrChatHash
          userServiceId
          organizationId
        }
        phoneNumber
        communicationState {
          id
          messageType
          isSeen
          appType
        }
        children {
          id
          email
          type
          jwt
          profileImage
          inviteExpireTime
          firstName
          middleName
          lastName
          preferredName
          pronouns
          prefix
          suffix
          isDevUser
          parentStatus(parentId: $id)
          isRequestAccepted
          sourceId
          familyInviteCode
          signInCode
          isBlocked
          blockMessage
          curriculumPrograms {
            id
            type
          }
          organization {
            id
            name
            tier {
              plan
              isFree
            }
            region
            academicYears {
              id
              isCurrentAcademicYear
            }
            curriculumPrograms {
              ...curriculumProgramBasicDetailsItem
              ...curriculumProgramBasicDetailsWithSubscriptionPlanItem
            }
          }
          allCourses {
            id
            academicYears {
              id
            }
            curriculumProgram {
              ...curriculumProgramBasicDetailsItem
            }
          }
          curriculumProgram {
            ...curriculumProgramBasicDetailsItem
          }
          academicYears(filters: { archivalState: ACTIVE }) {
            id
            startDate
            endDate
            isCurrentAcademicYear
            isUpcomingAcademicYear
            transitionReviewStatus
          }
        }
      }
    }
  }
  ${curriculumProgramFragment.curriculumProgramBasicDetailsWithSubscriptionPlan}
  ${curriculumProgramFragment.curriculumProgramBasicDetails}
`;

export const getOrganizationDetailsQuery = gql`
  query getOrganizationDetails($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationItem
        logo
      }
    }
  }
  ${organizationFragment.organizationDetails}
`;

export const getAssessmentTemplatesQuery = gql`
  query getAssessmentTemplates($curriculumIds: ID!) {
    node(id: $curriculumIds, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        taskTypes {
          id
          type
          label
          color
          displaySequence
          metaData
          templates {
            id
            grades {
              id
            }
          }
        }
      }
    }
  }
`;

export const getUpcomingVideoMeetingsQuery = gql`
  query getUpcomingVideoMeetings(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: UserCoursesCalendarFilter!
    $associatedEntitiesFilters: AssociatedEntitiesFilters!
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        calendarEvents(filters: $filters) {
          ...videoMeetingToastEvent
        }
        __typename
      }
      ... on Student {
        calendarEvents(filters: $filters) {
          ...videoMeetingToastEvent
        }
        __typename
      }
      __typename
    }
  }
  ${videoMeetingToastEventFragment}
`;

export const getCourseAcademicYearsQuery = gql`
  query getCourseAcademicYears($id: ID!) {
    node(id: $id, type: COURSE) {
      ... on Course {
        id
        academicYears {
          id
        }
      }
    }
  }
`;
